/**
 * Shared hook: load text.generation Skills once per workspace (single-flight).
 * Mirrors useCapabilityProviderOptions — no per-node duplicate requests.
 * Window focus invalidates so Admin bind/offline changes apply on refresh/refocus.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { invalidateSingleFlightCache } from './canvasRequestDedup'
import type { GenerateTextSkillLike } from './generateTextSkillFilter'
import { loadTextGenerationSkillsDeduped } from './generateTextSkills'

export function useCapabilityTextSkills(workspaceId: string, capability = 'text.generation') {
  const { i18n } = useTranslation()
  const locale = i18n.language
  const [skills, setSkills] = useState<GenerateTextSkillLike[]>([])
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [refreshTick, setRefreshTick] = useState(0)

  useEffect(() => {
    if (!workspaceId) return
    const onFocus = () => {
      invalidateSingleFlightCache(`textGenerationSkills:${workspaceId}`)
      invalidateSingleFlightCache(`textGenerationSkills:${workspaceId}:${locale || ''}`)
      setRefreshTick((n) => n + 1)
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [workspaceId, locale])

  useEffect(() => {
    if (!workspaceId || capability !== 'text.generation') {
      setSkills([])
      setError(false)
      setLoading(false)
      return
    }
    let cancelled = false
    setError(false)
    setLoading(true)
    loadTextGenerationSkillsDeduped(workspaceId, locale)
      .then((list) => {
        if (cancelled) return
        // Full catalog: prompt-only vs image paths filter in generateTextReadiness / resolveGenerateTextSkill.
        setSkills(list)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setSkills([])
        setError(true)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, capability, locale, refreshTick])

  const defaultSkill = useMemo(
    () => skills.find((s) => s.is_default) ?? skills[0] ?? null,
    [skills],
  )

  return {
    skills,
    defaultSkill,
    error,
    loading,
  }
}
